import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { authService } from '../services/authService.js';
import { ReactComponent as Logo } from '../icons/logo_letter.svg';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { analyticsEvents } from '../config/analyticsConfig.js';
import toast from 'react-hot-toast';
import './Login.css';

function Login() {
  usePageTitle('Login');
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();

  const formatPhoneInput = (value) => {
    // Remove tudo exceto números
    let numbers = value.replace(/\D/g, '');
    
    // Se não começar com 55, adiciona
    if (!numbers.startsWith('55') && numbers.length > 0) {
      numbers = '55' + numbers;
    }
    
    // Limita a 13 dígitos (55 + 11 dígitos)
    numbers = numbers.substring(0, 13);
    
    // Formata: +55 (XX) XXXXX-XXXX ou +55 (XX) XXXX-XXXX
    if (numbers.length === 0) return '';
    if (numbers.length <= 2) return `+${numbers}`;
    if (numbers.length <= 4) return `+${numbers.substring(0, 2)} (${numbers.substring(2)}`;
    if (numbers.length <= 9) return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4)}`;
    if (numbers.length === 13) {
      return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4, 9)}-${numbers.substring(9)}`;
    } else if (numbers.length >= 10) {
      return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4, 8)}-${numbers.substring(8)}`;
    }
    return `+${numbers}`;
  };

  const removePhoneMask = (phone) => {
    if (!phone) return '';
    // Remove tudo exceto números
    const numbers = phone.replace(/\D/g, '');
    // Retorna com +55
    if (numbers.startsWith('55')) {
      return `+${numbers}`;
    }
    return `+55${numbers}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setPhone(formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (isSignup) {
      // Validação para cadastro
      if (!name || !email || !phone || !password) {
        setError('Por favor, preencha todos os campos');
        return;
      }

      // Validação de nome
      if (name.trim().length < 3) {
        setError('Nome deve ter pelo menos 3 caracteres');
        return;
      }

      // Validação de telefone (formato básico)
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(phone) || phone.replace(/\D/g, '').length < 10) {
        setError('Por favor, insira um telefone válido');
        return;
      }

      // Validação de senha
      if (password.length < 6) {
        setError('Senha deve ter pelo menos 6 caracteres');
        return;
      }
    } else {
      // Validação para login
      if (!email || !password) {
        setError('Por favor, preencha todos os campos');
        return;
      }
    }

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor, insira um email válido');
      return;
    }

    setIsLoading(true);
    
    try {
      if (isSignup) {
        // Criar nova conta
        const result = await authService.signup({
          name: name.trim(),
          email: email.trim(),
          phone: removePhoneMask(phone),
          password,
        }, false);
        
        if (result.success) {
          if (result.emailVerified === false) {
            navigate('/verify-email', { state: { email: email.trim() } });
          } else {
            window.location.href = '/dashboard';
          }
        }
      } else {
        // Fazer login
        const result = await login(email, password);
        
        if (result.success) {
          if (result.emailVerified === false) {
            navigate('/verify-email', { state: { email: email.trim() } });
          } else {
            analyticsEvents.login('email');
          }
        } else {
          setError(result.message || 'Erro ao fazer login');
        }
      }
    } catch (err) {
      setError(err.message || (isSignup ? 'Erro ao criar conta' : 'Erro ao fazer login'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError('');
    setIsLoading(true);

    try {
      const result = await authService.signupWithGoogle(false);
      
      if (result.success) {
        analyticsEvents.login('google_signup');
        toast.success('Login com Google realizado com sucesso!');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err.message || 'Erro ao fazer login com Google');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      const result = await authService.loginWithGoogle();
      
      if (result.success) {
        analyticsEvents.login('google');
        toast.success('Login com Google realizado com sucesso!');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err.message || 'Erro ao fazer login com Google');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setName('');
    setEmail('');
    setPhone('');
    setPassword('');
  };

  return (
    <div className="login-container">
      <div className="login-background"></div>
      <div className="login-card">
        <div className="login-header">
          <div className="logo-wrapper">
            <Logo className="logo-svg" height='70px' />
          </div>
          <h2 className="login-title">{isSignup ? 'Criar conta' : 'Entrar'}</h2>
          <p className="login-subtitle">
            {isSignup ? 'Por favor, complete com suas informações pessoais.' : 'Digite suas credenciais para continuar.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {isSignup && (
            <div className="input-group">
              <label htmlFor="name">Nome</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Digite seu nome"
                disabled={isLoading}
                autoComplete="name"
              />
            </div>
          )}

          <div className="input-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Digite seu email"
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          {isSignup && (
            <div className="input-group">
              <label htmlFor="phone">Telefone</label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+55 (00) 00000-0000"
                disabled={isLoading}
                autoComplete="tel"
              />
            </div>
          )}

          <div className="input-group">
            <label htmlFor="password">Senha</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                disabled={isLoading}
                autoComplete={isSignup ? "new-password" : "current-password"}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              isSignup ? 'Criar conta' : 'Entrar'
            )}
          </button>

          <div className="divider">
            <span>ou</span>
          </div>

          <button 
            type="button" 
            className="google-button" 
            onClick={isSignup ? handleGoogleSignup : handleGoogleLogin}
            disabled={isLoading}
          >
            <svg className="google-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar com o Google
          </button>

          <div className="login-footer">
            {!isSignup && (
              <a 
                href="#forgot" 
                className="forgot-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/forgot-password');
                }}
              >
                Esqueceu a senha?
              </a>
            )}
            <button 
              type="button" 
              className="toggle-mode-link" 
              onClick={toggleMode}
              disabled={isLoading}
            >
              {isSignup ? 'Já tem uma conta? Entrar' : 'Não tem uma conta? Criar conta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
